import { usePaginatedQuery } from '@revenuemarket/react-query';
import { assert } from '@revenuemarket/utils';
import { useMutation, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';

import { getBankTransactionTypes } from '~apis/bank';
import { CardProjectionType } from '~apis/card';
import { AccountTransactionSearchParamFieldType } from '~apis/cash-flow-overview/transactions/AccountTransactionSearchParamFieldType';
import { getBankAccountTransactions } from '~apis/cash-flow-overview/transactions/getBankAccountTransactions';
import { getRecommendFilters } from '~apis/cash-flow-overview/transactions/getRecommendFilters';
import { getTransaction } from '~apis/cash-flow-overview/transactions/getTransaction';
import { getTransactionRecommendedLabels } from '~apis/cash-flow-overview/transactions/getTransactionRecommendedLabels';
import {
  FilterOperation,
  getTransactionsByPage,
  getFlatTransactionsByPage,
  RecommendFilter,
  LabeledBankAccountTransaction,
  MappingLabelType,
} from '~apis/cash-flow-overview/transactions/getTransactions';
import { patchLabellingBulk } from '~apis/labeling/patchLabellingBulk';
import { BankAccountCashFlowsQueryKey } from '~hooks/queries/bank-accounts/cashFlows/query';
import { queryClient } from '~libs/queryClient';
import { AccountType } from '~models/bank/enum';
import { CashFlowType } from '~models/cashFlow';
import { Page } from '~types/response';

import { AnalysisQueryKey } from '../analysis/query';
import { CASH_FLOW_KEY } from '../useGetCashFlow';

interface CompanyIdentifier {
  companyId: string;
}

interface TransactionIdentifier extends CompanyIdentifier {
  transactionId?: number;
}

interface BankAccountTransactionIdentifier extends TransactionIdentifier {
  accountId?: number;
}

interface ListParams {
  startDate: Dayjs;
  endDate: Dayjs;
  searchParam?: string;
  searchParamFieldTypes?: AccountTransactionSearchParamFieldType[];
  type?: CashFlowType;
  sort?: string[];
}

interface LabelingListParams extends ListParams {
  filter?: RecommendFilter;
}

export const TransactionsQueryKey = {
  default: () => ['transactions'],
  list: ({ companyId, request }: CompanyIdentifier & { request?: ListParams }) =>
    [...TransactionsQueryKey.default(), companyId, 'list', request].filter(Boolean),
  labelingList: ({ companyId, request }: CompanyIdentifier & { request?: LabelingListParams }) =>
    [...TransactionsQueryKey.list({ companyId }), 'labeling', request].filter(Boolean),
  flatLabelingList: ({
    companyId,
    request,
  }: CompanyIdentifier & { request?: LabelingListParams }) =>
    [...TransactionsQueryKey.list({ companyId }), 'labeling-flat', request].filter(Boolean),
  detail: ({ companyId, transactionId }: TransactionIdentifier) =>
    [...TransactionsQueryKey.list({ companyId }), 'detail', transactionId].filter(Boolean),
  recommendLabel: (identifier: BankAccountTransactionIdentifier) => [
    ...TransactionsQueryKey.detail(identifier),
    identifier.accountId,
    'recommendLabel',
  ],
  transactionTypes: (companyId: string) => [
    ...TransactionsQueryKey.default(),
    'transactionTypes',
    companyId,
  ],
};

const USE_GET_MAPPED_TAX_INVOICES = {
  default: (companyId: string) => ['mapped-tax-invoices', companyId],
};
export const afterLabelingActions = (companyId: string) => {
  queryClient.invalidateQueries([...CASH_FLOW_KEY.default]);
  queryClient.invalidateQueries([USE_GET_MAPPED_TAX_INVOICES.default(companyId)]);
  queryClient.invalidateQueries(TransactionsQueryKey.default());
  queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId: companyId }));
  queryClient.invalidateQueries(BankAccountCashFlowsQueryKey.summary({ companyId: companyId }));
  queryClient.invalidateQueries(AnalysisQueryKey.default());
};

export function useBulkLabeling(delayRefetch?: boolean, delayms?: number, doNotRefetch?: boolean) {
  const delay = delayms ?? 2000;
  return useMutation({
    mutationFn: patchLabellingBulk,
    onSuccess(_, variables) {
      if (doNotRefetch) {
        return;
      }
      if (delayRefetch) {
        setTimeout(() => {
          afterLabelingActions(variables.companyId);
        }, delay);
        return;
      }

      afterLabelingActions(variables.companyId);
    },
  });
}

export function useRecommendFilters(companyId: string, startDate: Dayjs, endDate: Dayjs) {
  return useQuery({
    queryKey: ['recommendFilters', companyId, startDate, endDate],
    queryFn: () => getRecommendFilters(companyId, startDate, endDate),
    enabled: !!companyId,
  });
}

export function useLabelingTransactions(
  params: CompanyIdentifier &
    LabelingListParams & {
      categories: number[];
      accounts: number[];
      customLabelIds: number[];
      detailedCategoryIds: number[];
      categoryLabelKeys: string[];
      businessEntities: { value: number; label?: string }[];
      onlyUnclassified?: boolean;
      onlyUnmapped?: boolean;
      excludeHiddenAccounts?: boolean;
      accountFilterOp?: FilterOperation;
      businessEntityIdFilterOp?: FilterOperation;
      customLabelFilterOp?: FilterOperation;
      categoryLabelFilterOp?: FilterOperation;
    },
) {
  const {
    companyId,
    startDate,
    endDate,
    type,
    categories,
    accounts,
    customLabelIds,
    detailedCategoryIds,
    categoryLabelKeys,
    businessEntities,
    searchParam,
    sort,
    onlyUnclassified,
    onlyUnmapped,
    filter,
    excludeHiddenAccounts,
    accountFilterOp,
    businessEntityIdFilterOp,
    customLabelFilterOp,
    categoryLabelFilterOp,
  } = params;

  return usePaginatedQuery({
    queryKey: TransactionsQueryKey.labelingList({ companyId, request: params }),
    queryFn: ({
      page,
      size,
      categories,
      searchParam,
      accounts,
      customLabelIds,
      categoryLabelKeys,
      businessEntities,
      detailedCategoryIds,
      onlyUnclassified,
      onlyUnmapped,
      excludeHiddenAccounts,
      accountFilterOp,
      businessEntityIdFilterOp,
      customLabelFilterOp,
      categoryLabelFilterOp,
    }) => {
      const clusterId = filter?.type === 'CLUSTER' ? Number(filter.key) : undefined;
      const mappingLabel = filter?.type === 'MAPPING_LABEL' ? filter.key : undefined;
      return getTransactionsByPage({
        companyId,
        startDate,
        endDate,
        page,
        categories,
        accounts,
        customLabelIds,
        categoryLabelKeys,
        businessEntityIds: businessEntities.map((entity) => entity.value),
        detailedCategoryIds,
        searchParam,
        size,
        type,
        sort,
        onlyUnclassified,
        onlyUnmapped,
        clusterId,
        mappingLabel,
        excludeHiddenAccounts,
        accountFilterOp,
        businessEntityIdFilterOp,
        customLabelFilterOp,
        categoryLabelFilterOp,
      });
    },
    defaultPageParams: {
      page: 0,
      size: 300,
      categories,
      accounts,
      searchParam,
      customLabelIds,
      businessEntities,
      detailedCategoryIds,
      onlyUnclassified,
      categoryLabelKeys,
      onlyUnmapped,
      excludeHiddenAccounts,
      accountFilterOp,
      businessEntityIdFilterOp,
      customLabelFilterOp,
      categoryLabelFilterOp,
    },
    options: {
      enabled: !!companyId && !!startDate && !!endDate,
      refetchOnMount: true,
    },
  });
}

export function useFlatLabelingTransactions(
  params: CompanyIdentifier &
    LabelingListParams & {
      categories: number[];
      accounts: number[];
      customLabelIds: number[];
      detailedCategoryIds: number[];
      categoryLabelKeys: string[];
      businessEntities: { value: number; label?: string }[];
      onlyUnclassified?: boolean;
      onlyUnmapped?: boolean;
      excludeHiddenAccounts?: boolean;
      accountFilterOp?: FilterOperation;
      businessEntityIdFilterOp?: FilterOperation;
      customLabelFilterOp?: FilterOperation;
      categoryLabelFilterOp?: FilterOperation;
      mappingLabelTypes?: MappingLabelType[];
      mappingLabelTypesFilterOp?: FilterOperation;
      transactionId?: number;
      cardProjectionType?: CardProjectionType;
    },
) {
  const {
    companyId,
    startDate,
    endDate,
    type,
    categories,
    accounts,
    customLabelIds,
    detailedCategoryIds,
    categoryLabelKeys,
    businessEntities,
    searchParam,
    searchParamFieldTypes,
    sort,
    onlyUnclassified,
    onlyUnmapped,
    filter,
    excludeHiddenAccounts,
    accountFilterOp,
    businessEntityIdFilterOp,
    customLabelFilterOp,
    categoryLabelFilterOp,
    mappingLabelTypes,
    mappingLabelTypesFilterOp,
    transactionId,
    cardProjectionType,
  } = params;

  return usePaginatedQuery({
    queryKey: TransactionsQueryKey.flatLabelingList({ companyId, request: params }),
    queryFn: ({
      page,
      size,
      categories,
      searchParam,
      searchParamFieldTypes,
      accounts,
      customLabelIds,
      categoryLabelKeys,
      businessEntities,
      detailedCategoryIds,
      onlyUnclassified,
      onlyUnmapped,
      excludeHiddenAccounts,
      accountFilterOp,
      businessEntityIdFilterOp,
      customLabelFilterOp,
      categoryLabelFilterOp,
      mappingLabelTypes,
      mappingLabelTypesFilterOp,
      transactionId,
      cardProjectionType,
    }) => {
      const clusterId = filter?.type === 'CLUSTER' ? Number(filter.key) : undefined;
      const mappingLabel = filter?.type === 'MAPPING_LABEL' ? filter.key : undefined;
      return getFlatTransactionsByPage({
        companyId,
        startDate,
        endDate,
        page,
        categories,
        accounts,
        customLabelIds,
        categoryLabelKeys,
        businessEntityIds: businessEntities.map((entity) => entity.value),
        detailedCategoryIds,
        searchParam,
        searchParamFieldTypes,
        size,
        type,
        sort,
        onlyUnclassified,
        onlyUnmapped,
        clusterId,
        mappingLabel,
        excludeHiddenAccounts,
        accountFilterOp,
        businessEntityIdFilterOp,
        customLabelFilterOp,
        categoryLabelFilterOp,
        mappingLabelTypes,
        mappingLabelTypesFilterOp,
        transactionId,
        cardProjectionType,
      });
    },
    defaultPageParams: {
      page: 0,
      size: 300,
      categories,
      accounts,
      searchParam,
      searchParamFieldTypes,
      customLabelIds,
      businessEntities,
      detailedCategoryIds,
      onlyUnclassified,
      categoryLabelKeys,
      onlyUnmapped,
      excludeHiddenAccounts,
      accountFilterOp,
      businessEntityIdFilterOp,
      customLabelFilterOp,
      categoryLabelFilterOp,
      mappingLabelTypes,
      mappingLabelTypesFilterOp,
      transactionId,
      cardProjectionType,
    },
    options: {
      enabled: !!companyId && !!startDate && !!endDate,
      refetchOnMount: true,
    },
  });
}

export function useGetBankLabelingTransactions(
  params: CompanyIdentifier &
    LabelingListParams & {
      categories: number[];
      accounts: number[];
      customLabelIds: number[];
      detailedCategoryIds: number[];
      categoryLabelKeys: string[];
      businessEntities: { value: number; label?: string }[];
      onlyUnclassified?: boolean;
      onlyUnmapped?: boolean;
      excludeHiddenAccounts?: boolean;
      accountFilterOp?: FilterOperation;
      businessEntityIdFilterOp?: FilterOperation;
      customLabelFilterOp?: FilterOperation;
      categoryLabelFilterOp?: FilterOperation;
      page: number;
      size: number;
    },
) {
  return useInfiniteQuery({
    queryKey: TransactionsQueryKey.labelingList({ companyId: params.companyId, request: params }),
    queryFn: ({ pageParam = 0 }) =>
      getTransactionsByPage({
        ...params,
        page: pageParam,
      }),
    getNextPageParam: (
      lastPage: { Data: { data: Page<LabeledBankAccountTransaction> } | null },
      pages,
    ) => {
      const totalPages = lastPage?.Data?.data?.totalPages ?? 0;
      const nextPage = pages.length;
      return nextPage < totalPages ? nextPage : undefined;
    },
  });
}

export function useBankAccountTransactions(
  params: CompanyIdentifier &
    ListParams & {
      accounts: number[];
      transactionTypes: string[];
    },
) {
  const { companyId, accounts, transactionTypes } = params;

  return usePaginatedQuery({
    queryKey: TransactionsQueryKey.list({ companyId, request: params }),
    queryFn: ({ page, size, accounts, transactionTypes }) =>
      getBankAccountTransactions({
        ...params,
        page,
        accounts,
        size,
        transactionTypes,
      }),
    defaultPageParams: {
      page: 0,
      size: 300,
      accounts,
      transactionTypes,
    },
    options: {
      enabled: !!companyId,
      refetchOnMount: true,
    },
  });
}

export function useTransaction(
  identifier: TransactionIdentifier & { accountType: AccountType } & {
    trigger?: boolean;
  },
) {
  return useQuery({
    queryKey: TransactionsQueryKey.detail(identifier),
    queryFn: () => {
      const { companyId, transactionId, accountType } = identifier;
      assert(transactionId != null);
      return getTransaction({ companyId, transactionId, accountType });
    },
    enabled: !!identifier.companyId && !!identifier.transactionId && (identifier.trigger ?? true),
  });
}

export function useTransactionRecommendedLabel(
  identifier: BankAccountTransactionIdentifier,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: TransactionsQueryKey.recommendLabel(identifier),
    queryFn: () => {
      const { companyId, accountId, transactionId } = identifier;
      assert(accountId != null);
      assert(transactionId != null);

      return getTransactionRecommendedLabels({ companyId, accountId, transactionId });
    },
    enabled: enabled && identifier.companyId != null && identifier.transactionId != null,
  });
}

export function useBankTransactionTypes(companyId: string) {
  return useQuery({
    queryKey: TransactionsQueryKey.transactionTypes(companyId),
    queryFn: () => getBankTransactionTypes({ companyId }),
    enabled: !!companyId,
  });
}
